import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Paper,
  Divider,
  Grid,
  Chip,
  TextField,
  MenuItem,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

interface SetFixedDepositDialogProps {
  open: boolean;
  riderId: string;
  riderName?: string;
  onClose: () => void;
  onSuccess: (fixedDepositAmount: number) => void;
}

interface SDConfigData {
  id: string;
  riderId?: string;
  vehicleModelId: string;
  city: string;
  modelName: string;
  brandName: string;
  batteryConfig: string;
  fixedDailyRent: number;
  baseSecurityDeposit: number;
  minimumSecurityDeposit: number;
  category?: string;
  imageUrl?: string;
}

const SetFixedDepositDialog: React.FC<SetFixedDepositDialogProps> = ({
  open,
  riderId,
  riderName,
  onClose,
  onSuccess,
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sdConfig, setSdConfig] = useState<SDConfigData | null>(null);
  const [availableConfigs, setAvailableConfigs] = useState<SDConfigData[]>([]);
  const [selectedVehicleModelId, setSelectedVehicleModelId] = useState<string>('');
  const [selectedExtraAmount, setSelectedExtraAmount] = useState<number>(0);

  // Fetch SD config when dialog opens
  useEffect(() => {
    if (open && riderId) {
      fetchSDConfig();
    }
  }, [open, riderId]);

  const fetchSDConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      setSdConfig(null);
      setAvailableConfigs([]);
      setSelectedVehicleModelId('');
      setSelectedExtraAmount(0);

      const RIDER_SD=import.meta.env.VITE_API_URL || 'https://testingdb.ev91riders.com/api'
      const url = `${RIDER_SD}/v1/security-deposit/fixed-deposit/config/${riderId}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        const configs: SDConfigData[] = response.data.data.configs || [];
        setAvailableConfigs(configs);

        const selectedConfig: SDConfigData | null = response.data.data.selectedConfig || null;
        if (selectedConfig) {
          setSdConfig(selectedConfig);
          setSelectedVehicleModelId(selectedConfig.vehicleModelId);
        } else if (configs.length === 1) {
          setSdConfig(configs[0]);
          setSelectedVehicleModelId(configs[0].vehicleModelId);
        }
      } else {
        setError(response.data.message || 'Failed to fetch SD configuration');
      }
    } catch (err: any) {
      console.error('Error fetching SD config:', err);
      setError(
        err.response?.data?.message ||
          'Failed to fetch security deposit configuration. Please ensure the rider city has SD configs available.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!sdConfig) {
      setError('Please select a vehicle model before setting the fixed deposit');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const fixedDepositAmount = sdConfig.baseSecurityDeposit + selectedExtraAmount;
      const RIDER_SD=import.meta.env.VITE_API_URL || 'https://testingdb.ev91riders.com/api'

      const response = await axios.post(
        `${RIDER_SD}/v1/security-deposit/fixed-deposit/${riderId}`,
        {
          fixedDepositAmount,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        onSuccess(fixedDepositAmount);
        onClose();
      } else {
        setError(response.data.message || 'Failed to set fixed deposit');
      }
    } catch (err: any) {
      console.error('Error setting fixed deposit:', err);
      setError(
        err.response?.data?.message || 'Failed to set fixed deposit. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set Fixed Deposit for Rider</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {/* Rider Info */}
          {riderName && (
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Rider
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {riderName}
              </Typography>
            </Box>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && !loading && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!loading && availableConfigs.length > 0 && (
            <Box>
              <TextField
                fullWidth
                select
                label="Select Vehicle Model"
                value={selectedVehicleModelId}
                onChange={(event) => {
                  const selectedId = event.target.value;
                  setSelectedVehicleModelId(selectedId);
                  const config = availableConfigs.find((item) => item.vehicleModelId === selectedId);
                  setSdConfig(config || null);
                }}
                helperText="Choose the SD model for this rider city"
                sx={{ mb: 2 }}
              >
                <MenuItem value="">
                  <em>Select a model</em>
                </MenuItem>
                {availableConfigs.map((config) => (
                  <MenuItem key={config.vehicleModelId} value={config.vehicleModelId}>
                    {config.brandName} {config.modelName}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {!loading && availableConfigs.length === 0 && (
            <Alert severity="info">
              No active SD configurations found for this rider&apos;s city. Please add SD configuration for the city first.
            </Alert>
          )}

          {/* SD Configuration Details */}
          {sdConfig && !loading && (
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <Stack spacing={2}>
                {/* Header with Model Info */}
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Vehicle Information
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Model Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {sdConfig.modelName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Brand
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {sdConfig.brandName}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                {/* Configuration Details */}
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Battery Config
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {sdConfig.batteryConfig || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        City
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {sdConfig.city}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Fixed Daily Rent
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                        ₹{sdConfig.fixedDailyRent.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Minimum SD
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        ₹{sdConfig.minimumSecurityDeposit.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Optional Additional Amount If Rider Don't Able To Submit Original Documents
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant={selectedExtraAmount === 500 ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() =>
                        setSelectedExtraAmount((prev) => (prev === 500 ? 0 : 500))
                      }
                    >
                      + ₹500
                    </Button>
                    <Button
                      size="small"
                      variant={selectedExtraAmount === 1000 ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() =>
                        setSelectedExtraAmount((prev) => (prev === 1000 ? 0 : 1000))
                      }
                    >
                      + ₹1000
                    </Button>
                  </Stack>
                </Box>

                <Divider />

                {/* Fixed Deposit Amount - Highlighted */}
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: '#e3f2fd',
                    borderLeft: '4px solid #1976d2',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Security Deposit
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#1976d2', mt: 0.5 }}
                  >
                    ₹{(sdConfig.baseSecurityDeposit + selectedExtraAmount).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                    {selectedExtraAmount > 0
                      ? `Base deposit ₹${sdConfig.baseSecurityDeposit.toFixed(2)} plus +₹${selectedExtraAmount} additional amount`
                      : 'This amount will be set as the Fixed Deposit for this rider'}
                  </Typography>
                </Box>

                {sdConfig.category && (
                  <Box sx={{ pt: 1 }}>
                    <Chip label={`Category: ${sdConfig.category}`} size="small" />
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Confirmation Note */}
          {sdConfig && !loading && (
            <Alert severity="info">
              Click "Confirm & Set" to set the Fixed Deposit of{' '}
              <strong>₹{(sdConfig.baseSecurityDeposit + selectedExtraAmount).toFixed(2)}</strong> for this rider
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!sdConfig || loading || submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : <VisibilityIcon />}
        >
          {submitting ? 'Setting...' : 'Confirm & Set'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetFixedDepositDialog;